/** Media and labeled fields stored on `payment_methods.account_info`. */

export type PaymentMethodCampoExtra = {
  etiqueta: string;
  valor: string;
};

export type PaymentMethodDisplayField = {
  label: string;
  value: string;
  href?: string;
};

export const PAYMENT_METHOD_TYPE_OPTIONS: Array<{ value: string; label: string; icon: string }> = [
  { value: 'transferencia', label: 'Cuenta bancaria', icon: '🏦' },
  { value: 'nequi', label: 'Nequi', icon: '💜' },
  { value: 'daviplata', label: 'Daviplata', icon: '🔴' },
  { value: 'link_pago', label: 'Link de pago', icon: '🔗' },
  { value: 'qr_code', label: 'Código QR', icon: '📱' },
  { value: 'efectivo', label: 'Efectivo', icon: '💵' },
  { value: 'tarjeta', label: 'Tarjeta / datáfono', icon: '💳' },
  { value: 'zelle', label: 'Zelle (USD)', icon: '💸' },
  { value: 'paypal', label: 'PayPal', icon: '🅿️' },
  { value: 'otro', label: 'Otro / personalizado', icon: '➕' },
];

export const TIPO_CUENTA_OPTIONS = ['Ahorros', 'Corriente', 'Nómina'];

/** Old/custom keys that should still open the Cuenta bancaria fields. */
const TRANSFERENCIA_ALIASES = new Set([
  'transferencia',
  'cuenta_bancaria',
  'cuenta',
  'banco',
  'transferencia_bancaria',
  'consignacion',
  'consignación',
  'wire',
]);

const KNOWN_FIELD_LABELS: Array<{ key: string; label: string }> = [
  { key: 'banco', label: 'Banco' },
  { key: 'tipo_cuenta', label: 'Tipo de cuenta' },
  { key: 'numero_cuenta', label: 'Número de cuenta' },
  { key: 'titular', label: 'Titular' },
  { key: 'nit', label: 'NIT / documento' },
  { key: 'telefono', label: 'Teléfono' },
  { key: 'email', label: 'Correo' },
  { key: 'url', label: 'Link de pago' },
  { key: 'proveedor', label: 'Proveedor' },
  { key: 'tipo', label: 'Tipo' },
  { key: 'ubicacion', label: 'Lugar' },
  { key: 'qr_data', label: 'Datos QR' },
];

const HIDDEN_ACCOUNT_KEYS = new Set([
  'qr_url',
  'qr_path',
  'archivos',
  'instrucciones_url',
  'instrucciones_nombre',
  'instrucciones_mime',
  'campos_extra',
  'details',
  'condiciones',
  'instrucciones',
]);

export function isHttpUrl(value: string | null | undefined): boolean {
  return typeof value === 'string' && /^https?:\/\//i.test(value.trim());
}

/** QR image URL on any method type (Nequi, transferencia, qr_code, …). */
export function getPaymentMethodQrUrl(accountInfo?: Record<string, unknown> | null): string {
  if (!accountInfo) return '';
  const url = typeof accountInfo.qr_url === 'string' ? accountInfo.qr_url.trim() : '';
  return isHttpUrl(url) ? url : '';
}

export function getPaymentMethodInstrucciones(
  accountInfo?: Record<string, unknown> | null
): string {
  if (!accountInfo) return '';
  for (const k of ['instrucciones', 'details', 'condiciones'] as const) {
    const v = accountInfo[k];
    if (typeof v === 'string' && v.trim()) return v;
  }
  return '';
}

export function getPaymentMethodCamposExtra(
  accountInfo?: Record<string, unknown> | null
): PaymentMethodCampoExtra[] {
  if (!accountInfo) return [];
  const raw = accountInfo.campos_extra;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const o = item as Record<string, unknown>;
      const etiqueta = String(o.etiqueta ?? o.label ?? o.nombre ?? '').trim();
      const valor = String(o.valor ?? o.value ?? '').trim();
      if (!etiqueta && !valor) return null;
      return { etiqueta, valor };
    })
    .filter((x): x is PaymentMethodCampoExtra => x != null);
}

function labelFromKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function displayField(label: string, value: string): PaymentMethodDisplayField {
  const v = value.trim();
  return isHttpUrl(v) ? { label, value: v, href: v } : { label, value: v };
}

/** Labeled rows to show clients (bank data + any custom named fields). */
export function getPaymentMethodDisplayFields(
  accountInfo?: Record<string, unknown> | null
): PaymentMethodDisplayField[] {
  if (!accountInfo) return [];
  const rows: PaymentMethodDisplayField[] = [];
  const seen = new Set<string>();

  for (const { key, label } of KNOWN_FIELD_LABELS) {
    const raw = accountInfo[key];
    if (typeof raw !== 'string' || !raw.trim()) continue;
    rows.push(displayField(label, raw));
    seen.add(key);
  }

  for (const extra of getPaymentMethodCamposExtra(accountInfo)) {
    if (!extra.etiqueta || !extra.valor) continue;
    rows.push(displayField(extra.etiqueta, extra.valor));
  }

  for (const [key, raw] of Object.entries(accountInfo)) {
    if (seen.has(key) || HIDDEN_ACCOUNT_KEYS.has(key)) continue;
    if (typeof raw !== 'string' || !raw.trim()) continue;
    rows.push(displayField(labelFromKey(key), raw));
  }

  const notas = getPaymentMethodInstrucciones(accountInfo);
  if (notas) rows.push(displayField('Notas', notas));

  return rows;
}

export function formatPaymentMethodDisplayFields(
  accountInfo?: Record<string, unknown> | null
): string {
  return getPaymentMethodDisplayFields(accountInfo)
    .map((f) => `${f.label}: ${f.value}`)
    .join('\n');
}

export function knownTypeSelectValue(methodType: string | undefined): string {
  const t = (methodType || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/-/g, '_');
  if (!t) return 'transferencia';
  if (TRANSFERENCIA_ALIASES.has(t)) return 'transferencia';
  if (PAYMENT_METHOD_TYPE_OPTIONS.some((o) => o.value === t)) return t;
  return 'otro';
}

export function isCuentaBancariaType(methodType: string | undefined): boolean {
  return knownTypeSelectValue(methodType) === 'transferencia';
}
